import { useEffect, useState } from 'react';
import { cityLabel, coinCategoryMeta } from '@/pages/le-coin/coinMeta';
import { addCoinReaction, removeCoinReaction, addCoinComment, getCoinComments, reportCoinPost, deleteCoinPostComment } from '@/services/coinService';
import type { ApiCoinComment, CoinPost } from '@/lib/types';
import { useAuth } from '@/hooks/useAuth';
import { removeCoinPostAsModerator } from '@/services/adminService';
import ConfirmModal from '@/pages/admin/components/ConfirmModal';
import { useNavigate } from 'react-router-dom';



interface CoinFeedCardProps {
  post: CoinPost;
  onCreateSortie: () => void;
  onNotify: (msg: string) => void;
  onRemoved: (postId: string) => void;
}

export default function CoinFeedCard({ post, onCreateSortie, onNotify, onRemoved, }: CoinFeedCardProps) {
  const meta = coinCategoryMeta[post.category];
  const { user } = useAuth();

  const [interested, setInterested] = useState(post.hasReacted ?? false);
  const [interestedCount, setInterestedCount] = useState(post.likes);
  const [showComments, setShowComments] = useState(false);
  const [apiComments, setApiComments] = useState<ApiCoinComment[]>([]);
  const [localComments, setLocalComments] = useState(post.comments);
  const [commentsLoaded, setCommentsLoaded] = useState(false);
  const [newComment, setNewComment] = useState('');
  const [sendingComment, setSendingComment] = useState(false);
  const [postToRemove, setPostToRemove] = useState(false);
  const [commentsCount, setCommentsCount] = useState(post.commentCount);

  const navigate = useNavigate();

  useEffect(() => {
    setInterested(post.hasReacted ?? false);
    setInterestedCount(post.likes);
  }, [post.hasReacted, post.likes]);

  const totalCommentCount = commentsCount;

  const toggleInterested = async () => {
    if (!user) {
      onNotify('Connecte-toi pour réagir');
      return;
    }
    const wasInterested = interested;
    setInterested(!wasInterested);
    setInterestedCount((c) => (wasInterested ? c - 1 : c + 1));
    try {
      if (wasInterested) {
        await removeCoinReaction(post.id);
      } else {
        await addCoinReaction(post.id);
      }
    } catch {
      setInterested(wasInterested);
      setInterestedCount((c) => (wasInterested ? c + 1 : c - 1));
    }
  };

  const handleToggleComments = async () => {
    const next = !showComments;
    setShowComments(next);
    if (next && !commentsLoaded) {
      try {
        const fetched = await getCoinComments(post.id);
        setApiComments(fetched);
        setCommentsLoaded(true);
      } catch {
        setCommentsLoaded(true);
      }
    }
  };

  const handleAddComment = async () => {
    const text = newComment.trim();
    if (!text || sendingComment) return;
    if (!user) {
      onNotify('Connecte-toi pour commenter');
      return;
    }
    setSendingComment(true);
    setNewComment('');
    try {
      const newCmt = await addCoinComment(post.id, text);
      setApiComments((prev) => [...prev, newCmt]);
      setCommentsCount((prev) => prev + 1);

      if (!commentsLoaded) setCommentsLoaded(true);
      setShowComments(true);
    } catch {
      setLocalComments((prev) => [
        ...prev,
        {
          id: `c-${Date.now()}`,
          authorAlias: user?.name ?? 'Vous',
          content: text,
          time: "À l'instant",
        },
      ]);
    } finally {
      setSendingComment(false);
    }
  }


  // Supprime un commentaire Le Coin.
  // Le backend vérifie que l'utilisateur est l'auteur du commentaire
  // ou possède le rôle Admin/Moderator.
  const handleDeleteComment = async (commentId: string) => {
    try {
      await deleteCoinPostComment(post.id, commentId);

      setApiComments((prev) =>
        prev.filter((comment) => comment.id !== commentId)
      );

      setCommentsCount((prev) => Math.max(0, prev - 1));

      onNotify('Commentaire supprimé');
    } catch {
      onNotify('Impossible de supprimer le commentaire');
    }
  };



  // Ouvre la confirmation avant le retrait du post Le Coin.
  // Cette action ne retire encore aucun contenu.
  const handleRemovePostAsModerator = () => {
    setPostToRemove(true);
  }

  // Retire le post Le Coin après confirmation.
  // Le backend vérifie obligatoirement le rôle Admin/Moderator.
  const confirmRemovePostAsModerator = async () => {
    try {
      await removeCoinPostAsModerator(post.id);

      setPostToRemove(false);
      onNotify('Publication retirée');
      onRemoved(post.id);
    } catch {
      onNotify('Impossible de retirer la publication');
    }
  }

  const handleReport = async () => {
    try {
      await reportCoinPost(post.id, 'Contenu inapproprié');
    } catch {
      // ignore
    }
    onNotify('Message signalé à la modération');
  }

  const displayedComments = commentsLoaded ? apiComments.map((c) => ({
    id: c.id,
    authorAlias: c.author?.displayName ?? c.author?.name ?? 'Utilisateur',
    content: c.content,
    time: new Date(c.createdAt).toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' }),
    isOwner: c.isOwner,
  }))
    : localComments;



  const handleShare = async () => {
    const shareData = {
      title: 'Le Coin · YonYon',
      url: 'https://yonyon.fr/le-coin',
    };

    try {
      if (navigator.share) {
        await navigator.share(shareData);
        return;
      }

      await navigator.clipboard.writeText(shareData.url);
      onNotify('Lien copié');
    } catch {
      // Annulation du partage par l'utilisateur
    }
  }

  return (
    <article className="bg-white border border-background-200 rounded-lg p-4 md:p-5 hover:border-background-300 transition-colors">
      <div
        onClick={handleToggleComments}
        className="cursor-pointer"
      >
        {/* Badges */}
        <div className="flex items-center gap-2 flex-wrap">
          <span className="inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-full bg-foreground-900 text-white text-xs font-semibold">
            <i className="ri-chat-smile-3-line"></i>
            Le Coin · Anonyme
          </span>
          <span className={`inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-full text-xs font-semibold ${meta.badgeClass}`}>
            <span className={`w-1.5 h-1.5 rounded-full ${meta.dotClass}`}></span>
            {meta.label}
          </span>
        </div>

        {/* Meta */}
        <div className="mt-2 flex items-center gap-2 flex-wrap text-xs text-foreground-400">
          <span>{post.time}</span>
          <span>·</span>
          <span className="flex items-center gap-1">
            <i className="ri-map-pin-line text-foreground-400"></i>
            {cityLabel(post.city)}
          </span>
        </div>

        {/* Content — clickable to comment */}
        <button
          type="button"
          onClick={handleToggleComments}
          className="mt-3 block w-full text-left cursor-pointer group"
          aria-label="Ouvrir les commentaires"
        >
          <p className="text-[15px] leading-relaxed text-foreground-800 group-hover:text-foreground-950 transition-colors">
            « {post.content} »
          </p>
          <span className="mt-1 inline-flex items-center gap-1 text-xs text-foreground-400 group-hover:text-primary-600 transition-colors">
            <i className="ri-chat-3-line"></i>
            {totalCommentCount > 0 ? 'Voir les commentaires' : 'Commenter'}
          </span>
        </button>

        {/* Stats */}
        <div className="mt-3 flex items-center gap-4 text-sm text-foreground-500">
          <span className="flex items-center gap-1.5">
            <i className={`${interested ? 'ri-heart-3-fill text-accent-600' : 'ri-heart-3-line'}`}></i>
            {interestedCount.toLocaleString('fr-FR')}
          </span>
          <button
            type="button"
            onClick={handleToggleComments}
            className="flex items-center gap-1.5 hover:text-foreground-700 transition-colors cursor-pointer"
          >
            <i className="ri-chat-3-line"></i>
            {totalCommentCount}
          </button>
          <div className="ml-auto flex items-center gap-3">
            {user && (user.isAdmin || user.isModerator) && (
              <button
                type="button"
                onClick={handleRemovePostAsModerator}
                className="flex items-center gap-1.5 text-xs text-red-600 hover:text-red-700 transition-colors cursor-pointer whitespace-nowrap"
                aria-label="Retirer la publication"
              >
                <i className="ri-delete-bin-line text-base"></i>
                Retirer
              </button>
            )}

            <button
              type="button"
              onClick={handleReport}
              className="flex items-center gap-1.5 text-xs text-foreground-400 hover:text-foreground-600 transition-colors cursor-pointer whitespace-nowrap"
              aria-label="Signaler"
            >
              <i className="ri-flag-line text-base"></i>
              Signaler
            </button>
          </div>
        </div>
      </div>

      {/* Actions */}
      <div className="mt-4 pt-3 border-t border-background-100 flex items-center gap-3">
        <button
          type="button"
          onClick={toggleInterested}
          className={`flex-1 px-4 py-2 rounded-md text-sm font-semibold transition-colors cursor-pointer whitespace-nowrap ${interested
            ? 'bg-accent-100 text-accent-800 hover:bg-accent-200'
            : 'bg-primary-500 text-white hover:bg-primary-600'
            }`}
        >
          <i
            className={`mr-1.5 ${interested ? 'ri-heart-3-fill' : 'ri-heart-3-line'
              }`}
          ></i>

          {interested ? 'J’aime ✓' : 'J’aime'}
        </button>

        <button
          type="button"
          onClick={handleShare}
          className="flex-1 px-4 py-2 rounded-md border border-primary-300 text-primary-700 text-sm font-semibold hover:bg-primary-50 transition-colors cursor-pointer whitespace-nowrap"
        >
          <i className="ri-share-forward-line mr-1.5"></i>
          Partager
        </button>
      </div>

      {/* Comments */}
      {showComments && (
        <div className="mt-3 pt-3 border-t border-background-100 animate-fade-in-up">
          <div className="flex flex-col gap-3">
            {displayedComments.map((comment) => (
              <div
                key={comment.id}
                className="group flex items-start gap-2.5"
              >
                <span className="w-7 h-7 shrink-0 rounded-full bg-background-100 text-foreground-400 flex items-center justify-center">
                  <i className="ri-shield-user-line text-sm"></i>
                </span>

                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="text-xs font-semibold text-foreground-700">
                      {comment.authorAlias}
                    </span>

                    <span className="text-[11px] text-foreground-400">
                      {comment.time}
                    </span>

                    {user &&
                      (comment.isOwner || user.isAdmin || user.isModerator) && (
                        <button
                          type="button"
                          onClick={() => handleDeleteComment(comment.id)}
                          className="ml-auto opacity-0 group-hover:opacity-100 focus:opacity-100 text-xs text-red-600 hover:text-red-700 transition-opacity cursor-pointer whitespace-nowrap"
                          aria-label="Supprimer le commentaire"
                        >
                          <i className="ri-delete-bin-line text-sm"></i>
                        </button>
                      )}
                  </div>

                  <p className="text-sm text-foreground-600 leading-relaxed">
                    {comment.content}
                  </p>
                </div>
              </div>
            ))}
          </div>

          {/* Nouveau commentaire */}
          <div className="mt-3 flex items-center gap-2">

            <label htmlFor="comment-input" className="sr-only">
              Nouveau commentaire
            </label>

            <input
              id="comment-input"
              type="text"
              value={newComment}
              onChange={(e) => {
                if (user) setNewComment(e.target.value);
              }}
              onClick={() => {
                if (!user) navigate('/auth');
              }}
              onKeyDown={(e) => {
                if (e.key === 'Enter' && user) handleAddComment();
              }}
              placeholder={user ? 'Répondre…' : 'Connecte-toi pour commenter'}
              maxLength={280}
              readOnly={!user}
              className={`
                w-full px-4 py-3 rounded-lg
                border border-background-300
                text-sm text-foreground-900
                placeholder:text-foreground-400
                focus:outline-none
                focus:border-primary-400
                focus:ring-2 focus:ring-primary-100
                ${!user ? 'cursor-pointer' : ''}
              `}
            />

            <button
              type="button"
              onClick={handleAddComment}
              disabled={!newComment.trim() || sendingComment || !user}
              className="px-3 py-2 rounded-md bg-primary-500 text-white text-sm font-semibold hover:bg-primary-600 transition-colors cursor-pointer whitespace-nowrap disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {sendingComment ? (
                <i className="ri-loader-4-line animate-spin"></i>
              ) : (
                'Envoyer'
              )}
            </button>
          </div>
        </div>
      )}

      {/* Confirmation avant le retrait d'une publication Le Coin par un Admin/Moderator */}
      <ConfirmModal
        open={postToRemove}
        title="Retirer cette publication ?"
        message="Cette publication ne sera plus visible sur l'application."
        confirmLabel="Retirer"
        tone="danger"
        onConfirm={confirmRemovePostAsModerator}
        onCancel={() => setPostToRemove(false)}
      />
    </article>
  );
}